import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/index.css";
import { AuthProvider } from "./contexts/AuthContext";
import { EntitlementsProvider } from "./contexts/EntitlementsContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollRestoration from "./components/ScrollRestoration";
import CookieConsentBanner from "./components/CookieConsentBanner";
import TermsGate from "./components/TermsGate"; // WO-53
import PromoInvitation from "./components/PromoInvitation"; // WO-71
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import VerifyEmail from "./pages/VerifyEmail";
// WO-53 — one privacy policy only: the website's (www.mid2bid.com/privacy-policy/).
function ExternalRedirect({ to }) {
  React.useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import Dashboard, { DashboardHome } from "./pages/Dashboard";
import NewsPage from "./pages/NewsPage";
import TendersPage from "./pages/TendersPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import CompaniesPage from "./pages/CompaniesPage";
import CompanyDetailPage from "./pages/CompanyDetailPage";
import ContactsPage from "./pages/ContactsPage";
import ContactDetailPage from "./pages/ContactDetailPage";
import ReportsPage from "./pages/ReportsPage";
import AdminDatabasePage from "./pages/AdminDatabasePage";
import AdminUsersPage from "./pages/AdminUsersPage";
import OpportunitiesPage from "./pages/OpportunitiesPage";
import SettingsPage from "./pages/SettingsPage";
import AlertsPage from "./pages/AlertsPage";
import SharedProjectPage from "./pages/SharedProjectPage";
import SharedTenderPage from "./pages/SharedTenderPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import GroupPage from "./pages/GroupPage";
import GroupInviteAccept from "./pages/GroupInviteAccept";
import ReferralDashboardPage from "./pages/ReferralDashboardPage";
import AdminReferralsPage from "./pages/AdminReferralsPage";
import { Toaster } from "./components/ui/sonner";
import { maybeInitAnalytics } from "./lib/analytics";
import "./lib/theme"; // Side-effect: applies stored dark/light theme before first paint

// Admin Pages
import {
  AdminExecutiveDashboard,
  AdminRevenue,
  AdminAccounts,
  AdminProductUsage,
  AdminMarketing,
  AdminFunnel,
  AdminDataOperations,
  AdminSystemHealth,
  AdminActivityLogs,
  AdminSettingsPage
} from "./pages/admin";
import AdminTenderSourcesPage from "./pages/admin/AdminTenderSourcesPage";
import AdminResearchPage from "./pages/admin/AdminResearchPage"; // WO-60

// Lazily load analytics if the user already consented.
if (typeof window !== "undefined") {
  maybeInitAnalytics();
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <HelmetProvider>
      <BrowserRouter>
        <ScrollRestoration />
        <LanguageProvider>
          <AuthProvider>
            <EntitlementsProvider>
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/verify" element={<VerifyEmail />} />
            <Route path="/privacy-policy" element={<ExternalRedirect to="https://www.mid2bid.com/privacy-policy/" />} />
            {/* WO-26 — the app had no terms page; the news module needs one. */}
            <Route path="/terms-of-service" element={<TermsOfService />} />
            {/* WO-27 — /terms is what people type and what other sites link to.
                Without this it falls through to the dashboard, silently. */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            
            {/* Shared Item Routes (Public) */}
            <Route path="/shared/project/:shareToken" element={<SharedProjectPage />} />
            <Route path="/shared/tender/:shareToken" element={<SharedTenderPage />} />
            {/* Group Invitation accept page (auth optional, redirects to login if needed) */}
            <Route path="/group-invite/:token" element={<GroupInviteAccept />} />
            
            {/* Protected Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardHome />} />
              <Route path="news" element={<NewsPage />} />
              <Route path="news/:id" element={<NewsPage />} />
              <Route path="tenders" element={<TendersPage />} />
              <Route path="tenders/:id" element={<TendersPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="projects/:id" element={<ProjectDetailPage />} />
              <Route path="companies" element={<CompaniesPage />} />
              <Route path="companies/:id" element={<CompanyDetailPage />} />
              <Route path="crm" element={<OpportunitiesPage />} />
              <Route path="crm/:id" element={<OpportunitiesPage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="contacts/:id" element={<ContactDetailPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/:id" element={<ReportsPage />} />
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="group" element={<GroupPage />} />
              <Route path="referrals" element={<ReferralDashboardPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="billing" element={<Navigate to="/dashboard/settings?tab=billing" replace />} />
              <Route path="payment/success" element={<PaymentSuccessPage />} />
              <Route path="payment/cancel" element={<PaymentCancelPage />} />
              {/* Admin-only routes (Phase 1.6) */}
              <Route element={<AdminRoute />}>
                <Route path="admin" element={<Navigate to="/dashboard/admin/executive" replace />} />
                <Route path="admin/database" element={<AdminDatabasePage />} />
                <Route path="admin/users" element={<AdminUsersPage />} />
                {/* New Admin Routes */}
                <Route path="admin/executive" element={<AdminExecutiveDashboard />} />
                <Route path="admin/revenue" element={<AdminRevenue />} />
                <Route path="admin/referrals" element={<AdminReferralsPage />} />
                <Route path="admin/accounts" element={<AdminAccounts />} />
                <Route path="admin/product-usage" element={<AdminProductUsage />} />
                <Route path="admin/marketing" element={<AdminMarketing />} />
                <Route path="admin/funnel" element={<AdminFunnel />} />
                <Route path="admin/data-operations" element={<AdminDataOperations />} />
                <Route path="admin/system-health" element={<AdminSystemHealth />} />
                <Route path="admin/activity-logs" element={<AdminActivityLogs />} />
                <Route path="admin/tender-sources" element={<AdminTenderSourcesPage />} />
                <Route path="admin/research" element={<AdminResearchPage />} />
                <Route path="admin/settings" element={<AdminSettingsPage />} />
              </Route>
            </Route>
            {/* Catch-all: unknown routes fall back to the dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <CookieConsentBanner />
          <TermsGate />
          <PromoInvitation />
          <Toaster position="top-right" richColors closeButton />
            </EntitlementsProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
    </HelmetProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
